import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import completeUserService from '../services/completeUserService';

/**
 * Hook personnalisé pour récupérer et utiliser le profil utilisateur complet
 * avec ses rôles, permissions et métadonnées des rôles
 *
 * @returns {Object} Un objet contenant le profil utilisateur complet et des fonctions utilitaires
 */
const useCompleteUser = () => {
    const { isAuthenticated, token, user } = useAuth();
    const [completeUser, setCompleteUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Récupère le profil utilisateur complet depuis l'API
     */
    const fetchCompleteUser = useCallback(async () => {
        if (!isAuthenticated || !token) {
            setCompleteUser(null);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const userData = await completeUserService.getCompleteProfile(token);
            setCompleteUser(userData);
        } catch (err) {
            console.error('Error fetching complete user:', err);
            setError(err.message || 'Failed to fetch user data');

            // En cas d'erreur, utiliser les données de base de l'utilisateur comme fallback
            if (user) {
                // Créer un objet completeUser minimal à partir des données de base
                const fallbackUser = {
                    ...user,
                    roles: [],
                    roleDetails: []
                };

                // Ajouter les rôles de base basés sur les flags booléens
                if (user.isAdmin) {
                    fallbackUser.roles.push('admin');
                    fallbackUser.roleDetails.push({
                        id: 'admin',
                        name: 'admin',
                        description: 'Administrateur',
                        isSystem: true,
                        isActive: true,
                        icon: 'AdminPanelSettings',
                        color: '#FF5722'
                    });
                }
                if (user.isOwner) {
                    fallbackUser.roles.push('owner');
                    fallbackUser.roleDetails.push({
                        id: 'owner',
                        name: 'owner',
                        description: 'Propriétaire',
                        isSystem: true,
                        isActive: true,
                        icon: 'Home',
                        color: '#2196F3'
                    });
                }
                if (user.isTenant) {
                    fallbackUser.roles.push('tenant');
                    fallbackUser.roleDetails.push({
                        id: 'tenant',
                        name: 'tenant',
                        description: 'Locataire',
                        isSystem: true,
                        isActive: true,
                        icon: 'Apartment',
                        color: '#4CAF50'
                    });
                }

                setCompleteUser(fallbackUser);
            } else {
                setCompleteUser(null);
            }
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, token, user]);

    // Récupérer le profil complet au chargement et quand l'authentification change
    useEffect(() => {
        fetchCompleteUser();
    }, [fetchCompleteUser]);

    /**
     * Vérifie si l'utilisateur a un rôle spécifique
     * @param {string} role - Le rôle à vérifier
     * @returns {boolean} True si l'utilisateur a le rôle, false sinon
     */
    const hasRole = useCallback((role) => {
        return completeUserService.hasRole(completeUser, role);
    }, [completeUser]);

    /**
     * Vérifie si l'utilisateur a au moins un des rôles spécifiés
     * @param {Array<string>} roles - Les rôles à vérifier
     * @returns {boolean} True si l'utilisateur a au moins un des rôles, false sinon
     */
    const hasAnyRole = useCallback((roles) => {
        return completeUserService.hasAnyRole(completeUser, roles);
    }, [completeUser]);

    /**
     * Vérifie si l'utilisateur a une permission spécifique
     * @param {string} permission - La permission à vérifier
     * @returns {boolean} True si l'utilisateur a la permission, false sinon
     */
    const hasPermission = useCallback((permission) => {
        return completeUserService.hasPermission(completeUser, permission);
    }, [completeUser]);

    /**
     * Vérifie si l'utilisateur a au moins une des permissions spécifiées
     * @param {Array<string>} permissions - Les permissions à vérifier
     * @returns {boolean} True si l'utilisateur a au moins une des permissions, false sinon
     */
    const hasAnyPermission = useCallback((permissions) => {
        return completeUserService.hasAnyPermission(completeUser, permissions);
    }, [completeUser]);

    /**
     * Récupère les détails d'un rôle spécifique de l'utilisateur
     * @param {string} roleName - Le nom du rôle
     * @returns {Object|null} Les détails du rôle ou null si non trouvé
     */
    const getRoleDetails = useCallback((roleName) => {
        if (!completeUser || !completeUser.roleDetails) {
            return null;
        }
        return completeUser.roleDetails.find(role => role.name === roleName) || null;
    }, [completeUser]);

    /**
     * Récupère tous les détails des rôles de l'utilisateur
     * @returns {Array} Tableau des détails des rôles
     */
    const getAllRoleDetails = useCallback(() => {
        return completeUser?.roleDetails || [];
    }, [completeUser]);

    /**
     * Vérifie si un rôle est actif
     * @param {string} roleName - Le nom du rôle
     * @returns {boolean} True si le rôle est actif, false sinon
     */
    const isRoleActive = useCallback((roleName) => {
        const roleDetail = getRoleDetails(roleName);
        return roleDetail ? roleDetail.isActive : false;
    }, [getRoleDetails]);

    /**
     * Vérifie si un rôle est un rôle système
     * @param {string} roleName - Le nom du rôle
     * @returns {boolean} True si le rôle est un rôle système, false sinon
     */
    const isSystemRole = useCallback((roleName) => {
        const roleDetail = getRoleDetails(roleName);
        return roleDetail ? roleDetail.isSystem : false;
    }, [getRoleDetails]);

    /**
     * Récupère la couleur d'un rôle
     * @param {string} roleName - Le nom du rôle
     * @returns {string|null} La couleur du rôle ou null si non trouvé
     */
    const getRoleColor = useCallback((roleName) => {
        const roleDetail = getRoleDetails(roleName);
        return roleDetail ? roleDetail.color : null;
    }, [getRoleDetails]);

    /**
     * Récupère l'icône d'un rôle
     * @param {string} roleName - Le nom du rôle
     * @returns {string|null} L'icône du rôle ou null si non trouvé
     */
    const getRoleIcon = useCallback((roleName) => {
        const roleDetail = getRoleDetails(roleName);
        return roleDetail ? roleDetail.icon : null;
    }, [getRoleDetails]);

    return {
        completeUser,
        loading,
        error,
        fetchCompleteUser,
        hasRole,
        hasAnyRole,
        hasPermission,
        hasAnyPermission,
        getRoleDetails,
        getAllRoleDetails,
        isRoleActive,
        isSystemRole,
        getRoleColor,
        getRoleIcon
    };
};

export default useCompleteUser;